(() => {
  const fetchBundleDoc = async (url) => {
    const response = await fetch(url);
    const html = await response.text();
    return new DOMParser().parseFromString(html, 'text/html');
  };

  if (!customElements.get('bundle-builder')) {
    class BundleBuilder extends HTMLElement {
      constructor() {
        super();
        this.packSize = parseInt(this.dataset.packSize, 10) || 0;
        this.optionDataElement = this.querySelector('script.option-data[type="application/json"]');
        this.colors = [...this.querySelectorAll('color-option')];
        this.sizePicker = this.querySelector('size-picker');
        this.addToCartButton = this.querySelector('[data-add-to-cart]');
        this.chooseForMeButton = this.querySelector('[data-choose-for-me]');

        this._cachedOptionData = null;

        this.addEventListener('size:changed', () => this.onSizeChanged());
        this.addEventListener('color:changed', () => this.updateReadyState());
        this.addToCartButton?.addEventListener('click', (e) => this.onAddToCart(e));
        this.chooseForMeButton?.addEventListener('click', () => this.surpriseMe());

        this.section = this.closest('.shopify-section');
        const parentPicker = this.section?.querySelector('product-variants.byob-parent-picker');
        parentPicker?.addEventListener('combined-listing:changed', (e) => this.onParentChanged(e));

        this.setColorsLocked(!this.sizePicker?.isReady);
        this.updateReadyState();
      }

      // --- state ---

      get optionData() {
        if (!this._cachedOptionData) {
          this._cachedOptionData = this.optionDataElement ? JSON.parse(this.optionDataElement.textContent) : [];
        }
        return this._cachedOptionData;
      }

      invalidateOptionData() {
        this._cachedOptionData = null;
      }

      get isBundleReady() {
        return (
          this.sizePicker?.isReady && this.colors.length >= this.packSize && this.colors.every((slot) => slot.isReady)
        );
      }

      // --- UI ---

      updateReadyState() {
        if (!this.addToCartButton) return;
        const ready = this.isBundleReady;
        this.addToCartButton.disabled = !ready;
        this.addToCartButton.classList.toggle('disabled', !ready);
      }

      setColorAvailability() {
        if (!this.sizePicker?.isReady) return;
        const byId = new Map(this.optionData.map((o) => [o.id, o]));
        for (const color of this.colors) {
          for (const input of color.querySelectorAll('input[type="radio"]')) {
            const option = byId.get(parseInt(input.dataset.optionValueId, 10));
            if (!option) continue;
            const unavailable = option.v_available === false;
            input.disabled = unavailable;
            input.classList.toggle('disabled', unavailable);
            input.hidden = option.exists === false;
          }
        }
      }

      setColorsLocked(locked) {
        for (const color of this.colors) {
          for (const input of color.querySelectorAll('input[type="radio"]')) {
            input.disabled = locked;
            // Locked (step 1 incomplete) must not reuse the OOS "disabled"
            // styling — availability classes are re-applied by
            // setColorAvailability() once a size is selected.
            if (locked) input.classList.remove('disabled');
          }
          color.classList.toggle('is-locked', locked);
        }
        const hint = this.querySelector('[data-colors-locked-hint]');
        if (hint) hint.hidden = !locked;
      }

      // --- events ---

      onSizeChanged() {
        this.invalidateOptionData();
        if (!this.sizePicker?.isReady) {
          this.setColorsLocked(true);
        } else {
          this.setColorsLocked(false);
          this.setColorAvailability();
        }
        this.updateReadyState();
      }

      surpriseMe() {
        const colors = this.optionData.filter((o) => o.position === 0 && o.available === true && o.exists !== false);
        if (!colors.length) return;
        for (const slot of this.colors) {
          const pick = colors[Math.floor(Math.random() * colors.length)];
          const input = slot.querySelector(`input[type="radio"][data-option-value-id="${pick.id}"]`);
          if (!input || input.disabled) continue;
          input.checked = true;
          input.dispatchEvent(new Event('change', {bubbles: true}));
        }
      }

      onParentChanged(e) {
        const sectionContent = e.target.sectionContent;
        const newSection = sectionContent?.querySelector('.shopify-section');
        if (!newSection || !this.section) return;
        this.section.replaceWith(newSection);
      }

      restoreState({sizes, colors}) {
        for (const fs of this.sizePicker?.fieldsets || []) {
          const position = fs.closest('size-option')?.dataset.optionPosition;
          const prev = sizes?.[position];
          if (!prev) continue;
          const input = [...fs.querySelectorAll('input[type="radio"]')].find(
            (i) => i.value === prev.value || i.dataset.optionValueId === prev.optionValueId
          );
          if (input && !input.disabled) {
            input.checked = true;
            const label = input.closest('size-option')?.querySelector('legend [data-js-current-selection]');
            if (label) label.textContent = input.value;
          }
        }

        queueMicrotask(() => {
          for (const slot of this.colors) {
            const prev = colors?.[slot.dataset.packIndex];
            if (!prev) continue;
            const input = [...slot.querySelectorAll('input[type="radio"]')].find(
              (i) => i.value === prev.value || i.dataset.optionValueId === prev.optionValueId
            );
            if (input && !input.disabled && !input.hidden) {
              input.checked = true;
              input.dispatchEvent(new Event('change', {bubbles: true}));
            }
          }
        });
      }

      async onAddToCart(e) {
        e.preventDefault();
        if (!this.isBundleReady || this.addToCartButton.classList.contains('working')) return;

        const bundleId = `byob_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
        const bundleTitle = this.dataset.parentTitle || 'Bundle';

        const items = this.colors
          .map((slot) => slot.variantId)
          .filter((id) => id != null)
          .map((id) => ({
            id,
            quantity: 1,
            properties: {_bundle_id: bundleId, _bundle_title: bundleTitle},
          }));

        if (items.length !== this.packSize) return;

        this.addToCartButton.classList.add('working');
        this.addToCartButton.disabled = true;

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({items}),
          });
          if (!res.ok) {
            const errorData = await res.json().catch(() => ({}));
            throw new Error(errorData.description || `HTTP ${res.status}`);
          }
          const result = await res.json();
          document.dispatchEvent(new CustomEvent('cart:updated', {detail: {items: result}}));
          document.dispatchEvent(new CustomEvent('add-to-cart', {detail: {items: result}}));
          window.refreshCart?.(true, true);
          this.dispatchEvent(new CustomEvent('bundle:added', {bubbles: true, detail: {items: result}}));
        } catch (error) {
          console.error('[bundle-builder] add to cart error:', error);
        } finally {
          this.addToCartButton.classList.remove('working');
          this.updateReadyState();
        }
      }
    }

    customElements.define('bundle-builder', BundleBuilder);
  }

  if (!customElements.get('size-picker')) {
    class SizePicker extends HTMLElement {
      constructor() {
        super();
        this.bundleBuilder = this.closest('bundle-builder');
        this.optionDataElement = this.bundleBuilder.optionDataElement;
        this.fieldsets = [...this.querySelectorAll('fieldset')];
        this.optionValues = [...this.defaultOptionValues];

        this.addEventListener('change', (e) => this.onChange(e));
        this.updateAvailability();
      }

      // --- state ---

      get isReady() {
        return this.fieldsets.length > 0 && this.fieldsets.every((fs) => fs.querySelector(':checked'));
      }

      get optionData() {
        return this.bundleBuilder.optionData;
      }

      get defaultOptionValues() {
        return JSON.parse(this.dataset.optionValues || '[]');
      }

      // --- DOM sync ---

      updateOptions() {
        if (!this.optionData?.length) return;
        const byKey = new Map(this.optionData.map((o) => [`${o.position}:${o.value}`, o]));
        for (const input of this.querySelectorAll('input')) {
          const position = parseInt(input.closest('size-option').dataset.optionPosition, 10);
          const option = byKey.get(`${position}:${input.value}`);
          if (option) input.dataset.optionValueId = option.id;
        }
      }

      updateOptionValues() {
        for (const fieldset of this.fieldsets) {
          const selected = fieldset.querySelector(':checked');
          if (!selected) continue;
          const position = parseInt(selected.closest('size-option').dataset.optionPosition, 10);
          this.optionValues[position] = parseInt(selected.dataset.optionValueId, 10) || null;
        }
        return this.optionValues;
      }

      setSelectedOptionValues(skipInput = null) {
        const skipName = skipInput?.name;
        for (const fieldset of this.fieldsets) {
          if (fieldset.querySelector(':checked')) continue;
          for (const option of this.optionData) {
            if (!option.selected) continue;
            const input = fieldset.querySelector(`input[type="radio"][data-option-value-id="${option.id}"]`);
            if (!input || (skipName && input.name === skipName)) continue;
            input.checked = true;
          }
        }
      }

      updateAvailability() {
        const byId = new Map(this.optionData.map((o) => [o.id, o]));
        for (const input of this.querySelectorAll('input')) {
          const option = byId.get(parseInt(input.dataset.optionValueId, 10));
          if (!option) continue;
          const unavailable = option.available === false;
          input.dataset.productAvailable = unavailable;
          input.disabled = unavailable;
          input.classList.toggle('disabled', unavailable);
        }
      }

      // --- events ---

      async onChange(e) {
        const target = e.target;
        const hasProductUrl = target.dataset.productUrl?.length > 0;

        if (hasProductUrl) this.setSelectedOptionValues(target);

        const optionValues = this.updateOptionValues();

        let productUrl;
        if (this.isReady && hasProductUrl) {
          productUrl = target.dataset.productUrl;
          this.dataset.productUrl = productUrl;
        } else {
          productUrl = this.dataset.productUrl;
        }
        const url = new URL(`${window.location.origin}${productUrl}`);
        url.searchParams.set('section_id', 'bundle-scripts');
        url.searchParams.set('option_values', optionValues.join(','));

        try {
          const doc = await fetchBundleDoc(url);
          const json = doc.querySelector('script.option-data[type="application/json"]');
          this.optionDataElement.textContent = json ? json.textContent : '[]';
          this.bundleBuilder.invalidateOptionData();
          if (this.isReady) this.updateOptions();
        } catch (error) {
          console.error('Error fetching option data', error);
          this.optionDataElement.textContent = '[]';
          this.bundleBuilder.invalidateOptionData();
        }
        this.bundleBuilder.dispatchEvent(new Event('size:changed'));
      }
    }

    customElements.define('size-picker', SizePicker);
  }

  if (!customElements.get('size-option')) {
    class SizeOption extends HTMLElement {
      constructor() {
        super();
        this.label = this.querySelector('legend [data-js-current-selection]');
        this.addEventListener('change', (e) => {
          if (this.label) this.label.textContent = e.target.value;
        });
      }
    }

    customElements.define('size-option', SizeOption);
  }

  if (!customElements.get('color-option')) {
    class ColorOption extends HTMLElement {
      constructor() {
        super();
        this.bundleBuilder = this.closest('bundle-builder');
        this.sizePicker = this.bundleBuilder.querySelector('size-picker');
        this.variantDataElement = this.querySelector('script[type="application/json"]');
        this.label = this.querySelector('legend [data-js-current-selection]');
        this.optionPosition = parseInt(this.dataset.optionPosition, 10);
        this.optionValues = [...this.sizePicker.optionValues];

        this.addEventListener('change', (e) => this.onChange(e));
        this.bundleBuilder.addEventListener('size:changed', () => this.onSizeChanged());
      }

      // --- state ---

      get variantData() {
        if (!this.variantDataElement) return null;
        try {
          const parsed = JSON.parse(this.variantDataElement.textContent);
          return parsed?.id ? parsed : null;
        } catch {
          return null;
        }
      }

      get variantId() {
        return this.variantData?.id ?? null;
      }

      get selectedInput() {
        return this.querySelector('input[type="radio"]:checked');
      }

      get optionData() {
        return this.bundleBuilder.optionData;
      }

      get optionExists() {
        const selected = this.selectedInput;
        if (!selected) return false;
        const id = parseInt(selected.dataset.optionValueId, 10);
        return this.optionData.find((o) => o.id === id)?.exists !== false;
      }

      get isReady() {
        return this.variantData?.available === true;
      }

      get isAvailable() {
        const selected = this.selectedInput;
        if (!selected) return false;
        const id = parseInt(selected.dataset.optionValueId, 10);
        return this.optionData.find((o) => o.id === id)?.available !== false;
      }

      // --- DOM sync ---

      buildURL(productUrl) {
        this.dataset.productUrl = productUrl;
        this.optionValues = [...this.sizePicker.optionValues];
        this.updateOptionValues();
        const url = new URL(`${window.location.origin}${productUrl}`);
        url.searchParams.set('section_id', 'bundle-scripts');
        url.searchParams.set('option_values', this.optionValues.join(','));
        return url;
      }

      updateOptions() {
        if (!this.optionData?.length) return;
        const byKey = new Map(this.optionData.map((o) => [`${o.position}:${o.value}`, o]));
        for (const input of this.querySelectorAll('input')) {
          const option = byKey.get(`${this.optionPosition}:${input.value}`);
          if (option) input.dataset.productUrl = option.product_url;
        }
      }

      updateOptionValues() {
        const selected = this.selectedInput;
        if (!selected) return this.optionValues;
        this.optionValues[this.optionPosition] = parseInt(selected.dataset.optionValueId, 10) || null;
        if (selected.dataset.productUrl) this.dataset.productUrl = selected.dataset.productUrl;
        return this.optionValues;
      }

      updateLabel(value) {
        if (this.label) this.label.textContent = value ?? this.selectedInput?.value ?? '';
      }

      uncheckInput() {
        const selected = this.selectedInput;
        if (selected) selected.checked = false;
        this.updateLabel('');
        if (this.variantDataElement) this.variantDataElement.textContent = '{}';
      }

      async fetchAndStoreVariant(productUrl) {
        const url = this.buildURL(productUrl);
        try {
          const doc = await fetchBundleDoc(url);
          const json = doc.querySelector('script.bundle-variant-data[type="application/json"]');
          this.variantDataElement.textContent = json ? json.textContent : '{}';
        } catch (error) {
          console.error('fetchVariantData failed:', error);
          this.variantDataElement.textContent = '{}';
        }
      }

      // --- events ---

      async onChange(e) {
        const target = e.target;
        if (!this.optionExists) {
          this.uncheckInput();
          this.dispatchEvent(new CustomEvent('color:changed', {bubbles: true}));
          return;
        }
        if (target.disabled) return;
        const productUrl = target.dataset.productUrl;
        if (productUrl?.length > 0) {
          await this.fetchAndStoreVariant(productUrl);
        }
        this.updateLabel(target.value);
        this.dispatchEvent(new CustomEvent('color:changed', {bubbles: true}));
      }

      async onSizeChanged() {
        this.updateOptions();
        if (!this.optionExists) {
          this.uncheckInput();
          this.dispatchEvent(new CustomEvent('color:changed', {bubbles: true}));
          return;
        }
        const selected = this.selectedInput;
        if (!selected || !this.isAvailable) {
          if (this.variantDataElement) this.variantDataElement.textContent = '{}';
          this.dispatchEvent(new CustomEvent('color:changed', {bubbles: true}));
          return;
        }
        const productUrl = selected.dataset.productUrl;
        if (!productUrl?.length) return;
        await this.fetchAndStoreVariant(productUrl);
        this.dispatchEvent(new CustomEvent('color:changed', {bubbles: true}));
      }
    }

    customElements.define('color-option', ColorOption);
  }

  if (!customElements.get('bundle-gallery')) {
    class BundleGallery extends HTMLElement {
      constructor() {
        super();
        this.defaultGrid = this.querySelector('[data-bundle-gallery-default]');
        this.bundleGrid = this.querySelector('[data-bundle-gallery-grid]');
        this.slides = new Map(
          [...this.querySelectorAll('[data-bundle-slide]')].map((el) => [el.dataset.bundleSlide, el])
        );

        this.bundleBuilder = this.closest('.shopify-section')?.querySelector('bundle-builder');
        if (!this.bundleBuilder) return;

        this.colorOptions = [...this.bundleBuilder.querySelectorAll('color-option')];

        this.bundleBuilder.addEventListener('color:changed', () => this.update());
        this.bundleBuilder.addEventListener('size:changed', () => this.update());
        this.update();
      }

      update() {
        const picked = this.colorOptions.filter((slot) => slot.selectedInput && slot.variantData);
        const hasAny = picked.length > 0;

        if (this.defaultGrid) this.defaultGrid.style.display = hasAny ? 'none' : '';
        if (this.bundleGrid) this.bundleGrid.style.display = hasAny ? '' : 'none';

        if (!hasAny) return;

        for (const slot of this.colorOptions) {
          const slide = this.slides.get(slot.dataset.packIndex);
          if (!slide) continue;
          const variant = slot.variantData;
          const src = variant?.featured_image_src;
          const label = slot.selectedInput?.value || '';
          const index = slot.dataset.packIndex;

          if (src) {
            this.fillSlide(slide, src, label, index);
          } else {
            this.emptySlide(slide, index);
          }
        }
      }

      fillSlide(slide, src, label, index) {
        slide.classList.remove('product-gallery-item--placeholder');

        let img = slide.querySelector('img');
        if (!img) {
          slide.innerHTML = `
            <figure class="lazy-image lazy-image--zoom-animation lazy-image--background lazyloaded">
              <img class="img" width="400" height="480" loading="lazy" />
            </figure>`;
          img = slide.querySelector('img');
        }

        const newSrc = this.imgUrl(src, 480);
        if (img.getAttribute('src') === newSrc) return;

        img.alt = `${label} - Pair ${index}`;
        img.src = newSrc;
        img.srcset = this.srcset(src);
        img.sizes = `(min-width: 768px) ${Math.floor(100 / Math.max(this.slides.size, 1))}vw, 100vw`;
      }

      emptySlide(slide, index) {
        if (slide.classList.contains('product-gallery-item--placeholder')) return;
        slide.classList.add('product-gallery-item--placeholder');
        slide.innerHTML = `
          <figure class="lazy-image lazy-image--background">
            <div class="bundle-gallery__placeholder">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 5V19M5 12H19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>Pair #${index}</span>
            </div>
          </figure>`;
      }

      srcset(src) {
        return [300, 400, 600, 800].map((w) => `${this.imgUrl(src, w)} ${w}w`).join(', ');
      }

      imgUrl(src, width) {
        if (!src) return '';
        const base = src.replace(/([?&])width=\d+&?/g, '$1').replace(/[?&]$/, '');
        return `${base}${base.includes('?') ? '&' : '?'}width=${width}`;
      }
    }

    customElements.define('bundle-gallery', BundleGallery);
  }
})();
